import { ArrowRight, Calendar, Loader2, Instagram, Youtube, X, RotateCw } from "lucide-react";
import {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FaFacebook, FaLinkedin, FaPinterest, FaTiktok } from "react-icons/fa6";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useStripe } from "../contexts/StripeContext";
import { useAnalyticsUiEnabled } from "../hooks/useAnalyticsUiEnabled";
import { useFirebaseUrl } from "../hooks/useFirebaseUrl";
import { useUpgradePage } from "../hooks/useUpgradePage";
import { doc, setDoc } from "firebase/firestore";
import { cn } from "../lib/utils";
import { readReturnToPostTask, stashReturnToPost } from "../lib/returnToPost";
import { beginWorkspaceOAuth } from "../lib/workspaceApi";
import { getAllValidConnectionsForUser, getPostForUser } from "../lib/utils";
import { db } from "../services/Firebase";
import { postConverter } from "../services/converters";
import { derivePostStatus } from "../utils/postStatus";
import { getConnectionErrorMessage } from "../utils/connectionErrorMessages";
import { ConnectionPost } from "../models/Post";
import {
  PlatformConnection,
  PlatformType,
  PostingScheduleConfig,
} from "../models/Platform";
import { PlatformConnectionIcon } from "../components/PlatformConnectionIcon";
import {
  ConnectionScheduleImpact,
  DisconnectAccountDialog,
} from "../components/DisconnectAccountDialog";
import { PostingScheduleEditor } from "../components/PostingScheduleEditor";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { Button } from "../components/ui/button";
import { useToast } from "../components/ui/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

type PlatformMeta = {
  name: string;
  icon: ReactElement;
};

const PLATFORM_ORDER: PlatformType[] = [
  "linkedin",
  "instagram",
  "facebook",
  "pinterest",
  "tiktok",
  "youtube",
];

const PLATFORM_META: Record<PlatformType, PlatformMeta> = {
  linkedin: { name: "LinkedIn", icon: <FaLinkedin className="h-4 w-4" /> },
  instagram: { name: "Instagram", icon: <Instagram className="h-4 w-4" /> },
  facebook: { name: "Facebook", icon: <FaFacebook className="h-4 w-4" /> },
  pinterest: { name: "Pinterest", icon: <FaPinterest className="h-4 w-4" /> },
  tiktok: { name: "TikTok", icon: <FaTiktok className="h-4 w-4" /> },
  youtube: { name: "YouTube", icon: <Youtube className="h-4 w-4" /> },
};

function StatusBadge({
  state,
}: {
  state: "expired" | "expiring" | "analytics";
}) {
  const dotClass = {
    expired: "bg-destructive",
    expiring: "bg-amber-500",
    analytics: "bg-amber-500",
  }[state];
  const label = {
    expired: "Expired",
    expiring: "Expires soon",
    analytics: "Analytics access needed",
  }[state];
  const textClass = {
    expired: "text-destructive",
    expiring: "text-amber-600",
    analytics: "text-amber-700",
  }[state];
  return (
    <span className={cn("inline-flex items-center gap-1.5 text-xs", textClass)}>
      <span className={cn("h-1.5 w-1.5 rounded-full", dotClass)} aria-hidden />
      {label}
    </span>
  );
}

function ConnectionRow({
  connection,
  onSchedule,
  onDisconnect,
  onReconnect,
  isDisconnecting,
  isReconnecting,
}: {
  connection: PlatformConnection;
  onSchedule: () => void;
  onDisconnect: () => void;
  onReconnect: () => void;
  isDisconnecting: boolean;
  isReconnecting: boolean;
}) {
  const { url: profileImageUrl } = useFirebaseUrl(connection.profileImage);
  const analyticsUiEnabled = useAnalyticsUiEnabled();
  const slotCount = connection.postingSchedule?.slots?.length ?? 0;
  const analyticsPermissionMissing =
    analyticsUiEnabled &&
    ((connection.analyticsAccess?.state === "reconnect_required" &&
      connection.analyticsAccess.reason === "analytics_permission") ||
      connection.analyticsAccess?.followerState === "reconnect_required");

  const statusState: "expired" | "expiring" | "analytics" | null =
    connection.status === "expired"
      ? "expired"
      : analyticsPermissionMissing
      ? "analytics"
      : connection.status === "expiring_soon"
      ? "expiring"
      : null;

  return (
    <div className="flex items-center gap-3 py-2.5">
      {connection.profileImage ? (
        profileImageUrl ? (
          <img
            src={profileImageUrl}
            alt={connection.profileName}
            className="h-7 w-7 rounded-full object-cover ring-1 ring-border"
          />
        ) : (
          <div className="h-7 w-7 rounded-full bg-muted animate-pulse" />
        )
      ) : (
        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-muted text-xs font-medium text-muted-foreground">
          {connection.profileName?.[0]?.toUpperCase()}
        </div>
      )}

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium text-foreground">
            {connection.profileName}
          </span>
          {statusState && <StatusBadge state={statusState} />}
        </div>
        {statusState === "expired" &&
        connection.expiredReason === "facebook_page_unselected" ? (
          <p className="text-xs text-muted-foreground mt-0.5">
            Facebook stopped sharing this Page. Reconnect, choose Edit
            settings in Facebook's window, and select it.
          </p>
        ) : (
          <p className="text-xs text-muted-foreground mt-0.5 tabular-nums">
            {analyticsUiEnabled &&
              connection.audience?.followerCount != null &&
              `${connection.audience.followerCount.toLocaleString()} followers · `}
            {slotCount === 0
              ? "No schedule set"
              : `${slotCount} post${slotCount === 1 ? "" : "s"} / week`}
          </p>
        )}
      </div>

      <div className="flex items-center gap-1 shrink-0">
        {(statusState === "expired" || analyticsPermissionMissing) && (
          <Button
            size="sm"
            onClick={onReconnect}
            disabled={isReconnecting}
            className="h-8 px-2.5 text-xs font-medium bg-[#FF5C35] text-white hover:bg-[#E02C00]"
          >
            {isReconnecting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <>
                <RotateCw className="h-3.5 w-3.5 mr-1.5" aria-hidden />
                {analyticsPermissionMissing ? "Reconnect for analytics" : "Reconnect"}
              </>
            )}
          </Button>
        )}
        {statusState !== "expired" && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onSchedule}
            className="h-8 px-2.5 text-xs font-normal text-muted-foreground hover:text-foreground"
          >
            <Calendar className="h-3.5 w-3.5 mr-1.5" aria-hidden />
            Schedule
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={onDisconnect}
          disabled={isDisconnecting}
          className="h-8 px-2.5 text-xs font-normal text-muted-foreground hover:text-foreground"
        >
          {isDisconnecting ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            "Disconnect"
          )}
        </Button>
      </div>
    </div>
  );
}

function PlatformRow({
  platform,
  connections,
  canConnect,
  connecting,
  onConnect,
  onReconnect,
  onSchedule,
  onDisconnect,
  revokeLoading,
}: {
  platform: PlatformType;
  connections: PlatformConnection[];
  canConnect: boolean;
  connecting: boolean;
  onConnect: () => void;
  onReconnect: (connectionId: string) => void;
  onSchedule: (id: string) => void;
  onDisconnect: (id: string) => void;
  revokeLoading: Record<string, boolean>;
}) {
  const meta = PLATFORM_META[platform];
  const hasConnections = connections.length > 0;

  return (
    <div className="border-t border-border first:border-t-0 py-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <span className="mt-0.5 text-foreground/70 shrink-0">
            {meta.icon}
          </span>
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground">{meta.name}</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {hasConnections
                ? `${connections.length} account${connections.length === 1 ? "" : "s"} connected`
                : platform === "instagram"
                  ? "Needs a Business or Creator account"
                  : "Not connected"}
            </p>
          </div>
        </div>
        <div className="shrink-0 sm:pl-4">
          <Button
            variant="outline"
            size="sm"
            onClick={onConnect}
            disabled={!canConnect || connecting}
            className="h-8"
          >
            {connecting ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                Connecting…
              </>
            ) : hasConnections ? (
              "Add account"
            ) : (
              "Connect"
            )}
          </Button>
        </div>
      </div>

      {hasConnections && (
        <div className="mt-3 space-y-0 divide-y divide-border/60 sm:pl-7">
          {connections.map((connection) => (
            <ConnectionRow
              key={connection.id}
              connection={connection}
              onSchedule={() => onSchedule(connection.id)}
              onDisconnect={() => onDisconnect(connection.id)}
              onReconnect={() => onReconnect(connection.id)}
              isDisconnecting={!!revokeLoading[connection.id]}
              isReconnecting={connecting}
            />
          ))}
        </div>
      )}
    </div>
  );
}

const SettingsPage = () => {
  const [connections, setConnections] = useState<PlatformConnection[]>([]);
  const [revokeLoading, setRevokeLoading] = useState<Record<string, boolean>>(
    {}
  );
  const [disconnectTarget, setDisconnectTarget] =
    useState<PlatformConnection | null>(null);
  const [disconnectImpact, setDisconnectImpact] =
    useState<ConnectionScheduleImpact | null>(null);
  const [disconnectImpactLoading, setDisconnectImpactLoading] = useState(false);
  const [disconnectImpactError, setDisconnectImpactError] = useState<
    string | null
  >(null);
  const [disconnectError, setDisconnectError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { currentUser, activeWorkspaceId } = useAuth();
  const { toast } = useToast();
  const userId = useMemo(() => activeWorkspaceId, [activeWorkspaceId]);
  const { navigateToUpgrade } = useUpgradePage();
  const { maxConnections: effectiveMax } = useStripe();
  const [editingScheduleFor, setEditingScheduleFor] = useState<string | null>(
    null
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const hasHandledNewConnection = useRef(false);
  const disconnectImpactRequest = useRef(0);

  // Reuse the editor's return hint for website scheduling/publishing tasks.
  // Those tasks resume after OAuth; ordinary editor connections keep the
  // existing Continue your post action.
  const [returnToPostId, setReturnToPostId] = useState<string | null>(searchParams.get("draftId"));
  const [returnPostIntent, setReturnPostIntent] = useState<string | undefined>(searchParams.get("intent") || undefined);
  const [connectedForPost, setConnectedForPost] = useState(false);
  useEffect(() => {
    const draftId = searchParams.get("draftId");
    if (draftId) stashReturnToPost(draftId, searchParams.get("intent") || undefined);
  }, [searchParams]);
  const [isContinuing, setIsContinuing] = useState(false);
  const hasConsumedReturnRef = useRef(false);
  useEffect(() => {
    if (hasConsumedReturnRef.current) return;
    if (searchParams.get("status") !== "success") return;
    hasConsumedReturnRef.current = true;
    const task = readReturnToPostTask();
    if (!task?.intent) readReturnToPostTask(true);
    if (task) {
      setReturnToPostId(task.draftId);
      setReturnPostIntent(task.intent);
      setConnectedForPost(true);
    }
  }, [searchParams]);

  // Return to the post being created. If the user now has exactly one connected
  // account, set it on the draft first so they land ready to publish — this is
  // the only place we auto-pick an account, scoped to the post-connect return.
  const handleContinuePost = useCallback(async () => {
    const draftId = returnToPostId;
    if (!draftId || isContinuing) return;
    setIsContinuing(true);
    try {
      if (userId && connections.length === 1) {
        const only = connections[0];
        const post = await getPostForUser(draftId, userId);
        if (post && !post.connections?.[only.id]) {
          const nextConnections: Record<string, ConnectionPost> = {
            ...(post.connections || {}),
            [only.id]: {
              connectionId: only.id,
              platform: only.platform,
              status: "pending",
              // No settings: the editor seeds them from what this channel
              // remembers from its last post.
            },
          };
          await setDoc(doc(db, "posts", draftId).withConverter(postConverter), {
            ...post,
            connections: nextConnections,
            status: derivePostStatus(nextConnections),
            updatedAt: Date.now(),
          });
        }
      }
    } catch (err) {
      console.error("Failed to preselect connection on draft:", err);
    }
    readReturnToPostTask(true);
    // A manual return before connecting should still let the user edit the
    // draft, rather than immediately sending them back to Connections.
    navigate(`/create-post?${new URLSearchParams({ draftId, ...(returnPostIntent && connections.length ? { intent: returnPostIntent } : {}) })}`);
  }, [returnToPostId, returnPostIntent, isContinuing, userId, connections, navigate]);

  const autoReturnedToPost = useRef(false);
  useEffect(() => {
    if (!connectedForPost || !returnPostIntent || !connections.length || autoReturnedToPost.current) return;
    autoReturnedToPost.current = true;
    void handleContinuePost();
  }, [connectedForPost, returnPostIntent, connections.length, handleContinuePost]);

  const fetchConnections = useCallback(async () => {
    if (!userId) return;
    try {
      const fetched = await getAllValidConnectionsForUser(userId);
      setConnections(fetched);
      setError((prev) => (prev === "Failed to load connections" ? null : prev));
    } catch (err) {
      console.error("Error fetching connections:", err);
      setError("Failed to load connections");
    }
  }, [userId]);

  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  useEffect(() => {
    if (hasHandledNewConnection.current) return;
    if (searchParams.get("status") !== "success") return;
    if (connections.length === 0) return;

    hasHandledNewConnection.current = true;
    // The account this connect touched: the most recently written one on the
    // returned platform. A reconnect keeps the original createdAt, so the
    // newest-created account overall can be a different platform entirely.
    const platform = searchParams.get("platform");
    const candidates = connections.filter((c) => c.platform === platform);
    const newest = (candidates.length ? candidates : connections).reduce(
      (a, b) =>
        (a.updatedAt ?? a.createdAt) >= (b.updatedAt ?? b.createdAt) ? a : b
    );
    if (!readReturnToPostTask()?.intent && !returnPostIntent && !newest.postingSchedule?.slots?.length) {
      setEditingScheduleFor(newest.id);
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("status");
        return next;
      },
      { replace: true }
    );
  }, [connections, searchParams, setSearchParams, returnPostIntent]);

  useEffect(() => {
    const errorCode = searchParams.get("error");
    if (!errorCode) return;
    setError(getConnectionErrorMessage(errorCode));
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("error");
        return next;
      },
      { replace: true }
    );
  }, [searchParams, setSearchParams]);

  const atLimit = connections.length >= effectiveMax;
  const overLimit = connections.length > effectiveMax;

  const [connectingAccount, setConnectingAccount] = useState<{
    platform: string;
    reconnectConnectionId?: string;
  } | null>(null);
  const handleConnect = (
    platform: string,
    opts?: { reconnectConnectionId?: string }
  ) => {
    if (connectingAccount) return;
    // Reconnecting an existing (expired) account re-runs OAuth and overwrites
    // the same connection doc in place, so it never adds to the account count —
    // don't block it on the connection limit.
    if (!opts?.reconnectConnectionId && atLimit) return;
    // Just flag the platform; the redirect fires from the effect below so the
    // "Connecting…" state paints first (a synchronous redirect skips the paint).
    setConnectingAccount({
      platform,
      reconnectConnectionId: opts?.reconnectConnectionId,
    });
  };
  useEffect(() => {
    if (!connectingAccount || !userId || !currentUser) return;
    void beginWorkspaceOAuth(
      currentUser,
      userId,
      connectingAccount.platform,
      { reconnectConnectionId: connectingAccount.reconnectConnectionId },
    ).catch((connectError) => {
      setError(connectError instanceof Error ? connectError.message : "Could not connect account.");
      setConnectingAccount(null);
    });
  }, [connectingAccount, userId, currentUser]);

  const openDisconnectDialog = useCallback(
    async (connectionId: string) => {
      const connection = connections.find((item) => item.id === connectionId);
      if (!connection) return;

      const requestId = disconnectImpactRequest.current + 1;
      disconnectImpactRequest.current = requestId;
      setDisconnectTarget(connection);
      setDisconnectImpact(null);
      setDisconnectImpactError(null);
      setDisconnectError(null);
      setDisconnectImpactLoading(true);

      try {
        if (!currentUser) throw new Error("Not authenticated");
        const idToken = await currentUser.getIdToken();
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE}/connections/${encodeURIComponent(
            connectionId,
          )}/schedule-impact`,
          {
            headers: { Authorization: `Bearer ${idToken}` },
          },
        );
        if (!response.ok) {
          throw new Error(
            "We couldn't check scheduled posts. You can still disconnect or try again.",
          );
        }
        const impact = (await response.json()) as ConnectionScheduleImpact;
        if (disconnectImpactRequest.current !== requestId) return;
        setDisconnectImpact(impact);
      } catch (impactError) {
        if (disconnectImpactRequest.current !== requestId) return;
        console.error("Error checking scheduled posts:", impactError);
        setDisconnectImpactError(
          impactError instanceof Error
            ? impactError.message
            : "We couldn't check scheduled posts. You can still disconnect or try again.",
        );
      } finally {
        if (disconnectImpactRequest.current === requestId) {
          setDisconnectImpactLoading(false);
        }
      }
    },
    [connections, currentUser],
  );

  const handleDisconnectDialogOpenChange = useCallback((open: boolean) => {
    if (open) return;
    disconnectImpactRequest.current += 1;
    setDisconnectTarget(null);
    setDisconnectImpact(null);
    setDisconnectImpactError(null);
    setDisconnectError(null);
    setDisconnectImpactLoading(false);
  }, []);

  const handleDisconnect = async () => {
    const connection = disconnectTarget;
    if (!connection) return;

    const connectionId = connection.id;
    setDisconnectError(null);
    setRevokeLoading((prev) => ({ ...prev, [connectionId]: true }));
    try {
      if (!currentUser) throw new Error("Not authenticated");
      const idToken = await currentUser.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE}/connections/revoke`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          // Keep the legacy flag during rollout so an older API revision also
          // performs the now-mandatory schedule cleanup.
          body: JSON.stringify({ connectionId, removeScheduledPosts: true }),
        }
      );
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          result.error ||
            "We couldn't disconnect this account. Please try again.",
        );
      }
      await fetchConnections();
      setError(null);
      handleDisconnectDialogOpenChange(false);
      const removedCount = Number(result.removedScheduledPostCount) || 0;
      const cleanupFailureCount = Number(result.taskDeletionFailureCount) || 0;
      const outcomeDescription = removedCount > 0
        ? `Removed this account from ${removedCount} post schedule${
            removedCount === 1 ? "" : "s"
          }. Other accounts and their publishing times were unchanged.`
        : undefined;
      const description = cleanupFailureCount
        ? [
            outcomeDescription,
            `${cleanupFailureCount} old background task${
              cleanupFailureCount === 1 ? "" : "s"
            } couldn't be deleted, but can no longer publish.`,
          ]
            .filter(Boolean)
            .join(" ")
        : outcomeDescription;
      toast({
        title: `${connection.profileName} disconnected`,
        ...(description ? { description } : {}),
      });
    } catch (err) {
      console.error("Error disconnecting account:", err);
      setDisconnectError(
        err instanceof Error
          ? err.message
          : "We couldn't disconnect this account. Please try again.",
      );
    } finally {
      setRevokeLoading((prev) => ({ ...prev, [connectionId]: false }));
    }
  };

  const handleSaveSchedule = useCallback(
    async (connectionId: string, schedule: PostingScheduleConfig) => {
      if (!currentUser) return;
      try {
        const idToken = await currentUser.getIdToken();
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE}/connections/${connectionId}/posting-schedule`,
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${idToken}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(schedule),
          }
        );
        if (!response.ok) throw new Error("Failed to save posting schedule");
        setEditingScheduleFor(null);
        await fetchConnections();
      } catch (err) {
        console.error("Error saving posting schedule:", err);
        setError("Failed to save posting schedule. Please try again.");
      }
    },
    [currentUser, fetchConnections]
  );

  return (
    <div className="min-h-full">
      <div className="max-w-2xl mx-auto px-6 py-12 md:py-16">
        {returnToPostId && (
          <div className="mb-8 flex items-center justify-between gap-3 rounded-xl border border-brand/20 bg-brand/5 px-4 py-3">
            <p className="text-sm text-foreground">
              {connections.length ? "Pick up where you left off — finish the post you were creating." : "Connect an account to continue with your post. Your draft is saved."}
            </p>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                size="sm"
                onClick={handleContinuePost}
                disabled={isContinuing}
                className="gap-1"
              >
                Continue your post
                {isContinuing ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <ArrowRight className="h-3.5 w-3.5" />
                )}
              </Button>
              <button
                type="button"
                onClick={() => setReturnToPostId(null)}
                aria-label="Dismiss"
                className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-black/[0.04] transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* Hero */}
        <header className="mb-10 text-center">
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight text-foreground">
            Connected accounts
          </h1>
          <p className="mt-3 text-base text-muted-foreground">
            Publish directly through each platform's official, secure connection
            — you approve every post before it goes live.
          </p>
        </header>

        {error && (
          <Alert variant="destructive" className="mb-8">
            <AlertTitle>Something didn't go through</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Capacity */}
        <section className="mb-10">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Capacity
            </p>
            <button
              type="button"
              onClick={() => navigateToUpgrade({ reason: "connection_limit" })}
              className={cn(
                "text-xs font-medium transition-colors",
                overLimit
                  ? "text-destructive hover:text-destructive/80"
                  : "text-brand hover:text-brand/80"
              )}
            >
              {overLimit ? "Upgrade required →" : "Get more accounts →"}
            </button>
          </div>
          <div className="flex flex-wrap items-baseline gap-x-1 text-sm">
            <span className="font-medium text-foreground tabular-nums">
              {connections.length} of {effectiveMax}
            </span>
            <span className="text-muted-foreground">
              account{effectiveMax === 1 ? "" : "s"} connected
              {atLimit && !overLimit ? " — at plan limit." : "."}
            </span>
          </div>
          {overLimit && (
            <p className="mt-2 text-sm text-destructive">
              You have {connections.length - effectiveMax} more account
              {connections.length - effectiveMax === 1 ? "" : "s"} than your
              plan allows. Keep the ones you want and disconnect the rest, or
              upgrade.
            </p>
          )}
        </section>

        {/* Channels */}
        <section className="space-y-0">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">
            Channels
          </p>
          <div>
            {PLATFORM_ORDER.map((platform) => {
              const platformConnections = connections.filter(
                (c) => c.platform === platform
              );
              return (
                <PlatformRow
                  key={platform}
                  platform={platform}
                  connections={platformConnections}
                  canConnect={!atLimit && !overLimit}
                  connecting={connectingAccount?.platform === platform}
                  onConnect={() => handleConnect(platform)}
                  onReconnect={(connectionId) =>
                    handleConnect(platform, { reconnectConnectionId: connectionId })
                  }
                  onSchedule={(id) => setEditingScheduleFor(id)}
                  onDisconnect={(id) => void openDisconnectDialog(id)}
                  revokeLoading={revokeLoading}
                />
              );
            })}
          </div>
        </section>
      </div>

      <DisconnectAccountDialog
        connection={disconnectTarget}
        open={disconnectTarget !== null}
        impact={disconnectImpact}
        isImpactLoading={disconnectImpactLoading}
        impactError={disconnectImpactError}
        isDisconnecting={
          disconnectTarget ? Boolean(revokeLoading[disconnectTarget.id]) : false
        }
        disconnectError={disconnectError}
        onOpenChange={handleDisconnectDialogOpenChange}
        onConfirm={() => void handleDisconnect()}
      />

      <Dialog
        open={editingScheduleFor !== null}
        onOpenChange={(open) => {
          if (!open) setEditingScheduleFor(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Posting schedule</DialogTitle>
            {(() => {
              const conn = connections.find(
                (c) => c.id === editingScheduleFor
              );
              if (!conn) return null;
              return (
                <DialogDescription asChild>
                  <div className="flex items-center gap-2">
                    <PlatformConnectionIcon
                      platform={conn.platform}
                      profileImage={conn.profileImage}
                      profileName={conn.profileName}
                      size="sm"
                      disableTooltip
                    />
                    <span>{conn.profileName}</span>
                  </div>
                </DialogDescription>
              );
            })()}
          </DialogHeader>
          {editingScheduleFor &&
            (() => {
              const conn = connections.find(
                (c) => c.id === editingScheduleFor
              );
              if (!conn) return null;
              return (
                <PostingScheduleEditor
                  connectionId={conn.id}
                  platform={conn.platform}
                  initialSchedule={conn.postingSchedule}
                  onSave={(schedule) => handleSaveSchedule(conn.id, schedule)}
                  onCancel={() => setEditingScheduleFor(null)}
                />
              );
            })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SettingsPage;
